/* SplashMaze — sign-in backdrop. Four quadrants of a randomly carved maze
   draw in from center outward, with a pulsing heart at the middle.
   Ported verbatim from design_handoff_daedalus/design_files/brand.jsx. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onAppReady } from '@/lib/bootSignal';
import { DaedalusWordmark } from './wordmarks';
import { createSplashScheduler } from './splashPlan';

type Props = {
  onDone?: () => void;
  /** Brand-moment floor: never dismiss before this, even if the app is ready. */
  minMs?: number;
  /** Hard ceiling: ALWAYS dismiss by this (pre-2026-07 fixed durations). */
  maxMs?: number;
  /** Repeat-visit / reduced-motion variant: static finished frame, no draw. */
  quick?: boolean;
};

type Seg = { d: string; dist: number; len: number };

function carveQuadrant(x0: number, y0: number, x1: number, y1: number, CELL: number) {
  const cols = Math.floor((x1 - x0) / CELL);
  const rows = Math.floor((y1 - y0) / CELL);
  const visited = Array.from({ length: rows }, () => new Array<boolean>(cols).fill(false));
  const walls: [number, number, number, number][] = [];
  const stack: [number, number][] = [[Math.floor(Math.random() * cols), Math.floor(Math.random() * rows)]];
  visited[stack[0][1]][stack[0][0]] = true;
  while (stack.length) {
    const [cxg, cyg] = stack[stack.length - 1];
    const dirs: [number, number][] = ([[1, 0], [-1, 0], [0, 1], [0, -1]] as [number, number][]).sort(
      () => Math.random() - 0.5
    );
    let moved = false;
    for (const [dx, dy] of dirs) {
      const nx = cxg + dx,
        ny = cyg + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      if (visited[ny][nx]) continue;
      visited[ny][nx] = true;
      const ax = x0 + cxg * CELL + CELL / 2;
      const ay = y0 + cyg * CELL + CELL / 2;
      const bx = x0 + nx * CELL + CELL / 2;
      const by = y0 + ny * CELL + CELL / 2;
      walls.push([ax, ay, bx, by]);
      stack.push([nx, ny]);
      moved = true;
      break;
    }
    if (!moved) stack.pop();
  }
  return walls;
}

function buildSplashMaze(): { segs: Seg[]; maxDist: number } {
  const W = 1000,
    H = 1000,
    CELL = 40,
    MARGIN = 80;
  const cx = W / 2,
    cy = H / 2;
  const q1 = carveQuadrant(MARGIN, MARGIN, cx - 40, cy - 40, CELL);
  const q2 = carveQuadrant(cx + 40, MARGIN, W - MARGIN, cy - 40, CELL);
  const q3 = carveQuadrant(MARGIN, cy + 40, cx - 40, H - MARGIN, CELL);
  const q4 = carveQuadrant(cx + 40, cy + 40, W - MARGIN, H - MARGIN, CELL);

  const outerFrame: [number, number, number, number][] = [
    [MARGIN, MARGIN, W - MARGIN, MARGIN],
    [W - MARGIN, MARGIN, W - MARGIN, H - MARGIN],
    [W - MARGIN, H - MARGIN, MARGIN, H - MARGIN],
    [MARGIN, H - MARGIN, MARGIN, MARGIN],
  ];
  const armature: [number, number, number, number][] = [
    ...outerFrame,
    [cx, MARGIN, cx, cy - 60],
    [cx, cy + 60, cx, H - MARGIN],
    [MARGIN, cy, cx - 60, cy],
    [cx + 60, cy, W - MARGIN, cy],
    [cx - 40, cy - 40, cx + 40, cy - 40],
    [cx + 40, cy - 40, cx + 40, cy + 40],
    [cx + 40, cy + 40, cx - 40, cy + 40],
    [cx - 40, cy + 40, cx - 40, cy - 40],
  ];

  const all = [...q1, ...q2, ...q3, ...q4, ...armature];
  const segs: Seg[] = all.map(([x1, y1, x2, y2]) => {
    const mx = (x1 + x2) / 2,
      my = (y1 + y2) / 2;
    const dist = Math.hypot(mx - cx, my - cy);
    const len = Math.hypot(x2 - x1, y2 - y1);
    return { d: `M ${x1} ${y1} L ${x2} ${y2}`, dist, len };
  });
  const maxDist = Math.max(...segs.map((s) => s.dist));
  return { segs, maxDist };
}

const drawSpan = 3.8;
const drawDur = 0.55;

export function SplashMaze({ onDone, minMs = 2600, maxMs = 6500, quick = false }: Props) {
  const [done, setDone] = useState(false);
  const [held, setHeld] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const [replay, setReplay] = useState(0);
  // The timer and tap-to-dismiss race; onDone must fire exactly once.
  const firedRef = useRef(false);

  // prefers-reduced-motion: skip the draw choreography and hold a brief
  // static frame instead. window/matchMedia are missing in the node test
  // environment → full run.
  const reducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const finish = useCallback(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    setDone(true);
    onDone?.();
  }, [onDone]);

  const isStatic = quick || reducedMotion;
  useEffect(() => {
    if (held) return; // dev hold pauses the scheduler too (effect re-runs on release)
    const eff = reducedMotion
      ? { minMs: Math.min(minMs, 400), maxMs: Math.min(maxMs, 800) } // preserve old clamp
      : { minMs, maxMs };
    const dispose = createSplashScheduler({
      ...eff,
      onFinish: finish,                 // firedRef in finish keeps this once-only
      onAppReady,                       // from '@/lib/bootSignal'
    });
    const d = setTimeout(() => setDrawn(true), isStatic ? 0 : 4350);
    return () => {
      dispose();
      clearTimeout(d);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [held, replay]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') finish();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { segs, maxDist } = useMemo(() => buildSplashMaze(), [replay]);

  return (
    // The splash blocks all input while visible — any tap/click skips it.
    <div className="splash-wrap" data-done={done} data-held={held} data-drawn={drawn} data-quick={isStatic} onClick={finish}>
      {import.meta.env.DEV && (
        <div className="splash-hold-ctl" onClick={(e) => e.stopPropagation()}>
          <button className="splash-hold-btn" onClick={() => setHeld((h) => !h)}>
            {held ? 'release' : 'hold'}
          </button>
          <button
            className="splash-hold-btn"
            onClick={() => {
              firedRef.current = false;
              setHeld(false);
              setDone(false);
              setDrawn(false);
              setReplay((r) => r + 1);
            }}
          >
            replay
          </button>
        </div>
      )}
      <div className="splash-wordmark">
        <DaedalusWordmark size={34} />
      </div>

      <svg viewBox="0 0 1000 1000" className="splash-svg" aria-label="Daedalus loading" preserveAspectRatio="xMidYMid meet">
        {([
          [40, 40],
          [960, 40],
          [40, 960],
          [960, 960],
        ] as [number, number][]).map(([x, y], i) => (
          <g key={i} stroke="currentColor" strokeWidth="1.4" opacity=".55">
            <line x1={x - 14} y1={y} x2={x + 14} y2={y} />
            <line x1={x} y1={y - 14} x2={x} y2={y + 14} />
          </g>
        ))}

        <g fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square" strokeLinejoin="miter">
          {segs.map((s, i) => (
            <path
              key={i}
              d={s.d}
              className="maze-seg"
              style={{
                strokeDasharray: Math.max(s.len, 20),
                strokeDashoffset: Math.max(s.len, 20),
                animationDelay: `${(s.dist / maxDist) * drawSpan}s`,
                animationDuration: `${drawDur}s`,
              }}
            />
          ))}
        </g>

        <circle cx="500" cy="500" r="6" fill="#f3c94a" className="splash-heart" />
        <circle cx="500" cy="500" r="16" fill="none" stroke="#f3c94a" strokeWidth="1.4" opacity=".6" className="splash-halo" />
      </svg>

      <div className="splash-meta">
        <div className="splash-cap">
          drafting the labyrinth
          <span className="dots">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </div>
      </div>

      <button className="splash-skip" aria-label="Skip intro" onClick={finish}>
        skip&nbsp;·&nbsp;esc
      </button>

      <style>{`
        .splash-wrap {
          position: fixed; inset: 0; z-index: 200;
          background: var(--bg-1);
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          color: var(--accent);
          animation: splashFadeIn .3s ease both;
        }
        .splash-wrap::before {
          content: ''; position: absolute; inset: 0;
          --cell: calc(min(92vmin, 1100px) / 25);
          --maze-half: calc(min(92vmin, 1100px) / 2);
          background-image:
            linear-gradient(var(--rule) 1px, transparent 1px),
            linear-gradient(90deg, var(--rule) 1px, transparent 1px);
          background-size: var(--cell) var(--cell);
          background-position: 50% 50%;
          opacity: .18; pointer-events: none; z-index: 0;
          clip-path: inset(0 0 0 0);
          transition: clip-path 1.1s cubic-bezier(.7,0,.3,1);
        }
        .splash-wrap[data-drawn="true"]::before {
          clip-path: inset(calc(50% - var(--maze-half)) calc(50% - var(--maze-half)));
        }
        .splash-wrap::after {
          content: ''; position: absolute; inset: 0;
          --cell5: calc(min(92vmin, 1100px) / 5);
          --maze-half: calc(min(92vmin, 1100px) / 2);
          background-image:
            linear-gradient(var(--rule) 1px, transparent 1px),
            linear-gradient(90deg, var(--rule) 1px, transparent 1px);
          background-size: var(--cell5) var(--cell5);
          background-position: 50% 50%;
          opacity: .28; pointer-events: none; z-index: 0;
          clip-path: inset(0 0 0 0);
          transition: clip-path 1.1s cubic-bezier(.7,0,.3,1);
        }
        .splash-wrap[data-drawn="true"]::after {
          clip-path: inset(calc(50% - var(--maze-half)) calc(50% - var(--maze-half)));
        }
        .splash-wrap[data-done="true"] { animation: splashFadeOut .5s ease forwards; pointer-events: none; }
        .splash-wrap[data-held="true"] .maze-seg,
        .splash-wrap[data-held="true"] .splash-heart,
        .splash-wrap[data-held="true"] .splash-halo,
        .splash-wrap[data-held="true"] .splash-cap .dots span { animation-play-state: paused !important; }
        .splash-hold-ctl {
          position: absolute; top: 20px; right: 20px; z-index: 5;
          display: flex; gap: 8px;
        }
        .splash-hold-btn {
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px; letter-spacing: .2em; text-transform: uppercase;
          padding: 6px 12px; background: var(--bg-2); color: var(--ink-2);
          border: 1px solid var(--rule); cursor: pointer;
        }
        .splash-hold-btn:hover { color: var(--accent); border-color: var(--accent); }
        .splash-skip {
          position: absolute; right: 20px; bottom: 20px; z-index: 5;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px; letter-spacing: .2em; text-transform: uppercase;
          padding: 6px 12px; background: transparent; color: var(--ink-3);
          border: 1px solid var(--rule); cursor: pointer;
          opacity: 0; animation: capIn .5s ease 1.2s forwards;
        }
        .splash-skip:hover, .splash-skip:focus-visible { color: var(--accent); border-color: var(--accent); }
        /* quick variant = the reduced-motion finished frame, minus the media query */
        .splash-wrap[data-quick="true"] .maze-seg { animation: none; stroke-dashoffset: 0 !important; }
        .splash-wrap[data-quick="true"]::before,
        .splash-wrap[data-quick="true"]::after { transition: none; }
        .splash-wrap[data-quick="true"] .splash-wordmark,
        .splash-wrap[data-quick="true"] .splash-cap { animation: none; opacity: 1; }
        .splash-wrap[data-quick="true"] .splash-skip { animation: none; opacity: 1; }
        @keyframes splashFadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes splashFadeOut { to { opacity: 0; transform: scale(1.03); } }
        .splash-svg { width: min(92vmin, 1100px); height: min(92vmin, 1100px); position: relative; z-index: 1; }

        .maze-seg {
          animation-name: mazeDraw;
          animation-timing-function: cubic-bezier(.7,0,.3,1);
          animation-fill-mode: forwards;
        }
        @keyframes mazeDraw { to { stroke-dashoffset: 0; } }

        .splash-heart { animation: splashHeart 1.3s ease-in-out infinite; transform-origin: 500px 500px; }
        .splash-halo  { animation: splashHalo 1.8s ease-out infinite; transform-origin: 500px 500px; transform-box: fill-box; }
        @keyframes splashHeart { 0%, 100% { opacity: 1 } 50% { opacity: .4 } }
        @keyframes splashHalo  { 0% { r: 10; opacity: .9 } 100% { r: 36; opacity: 0 } }

        .splash-meta {
          position: absolute; left: 50%;
          top: calc(50% + min(46vmin, 550px) - min(3.68vmin, 44px));
          transform: translate(-50%, -100%);
          text-align: center; z-index: 2;
          color: var(--ink-2); padding-bottom: 4px;
        }
        .splash-wordmark {
          position: absolute; z-index: 2; left: 50%;
          bottom: calc(50% + min(46vmin, 550px) - min(3.68vmin, 44px));
          transform: translate(-50%, 50%);
          color: var(--ink); text-transform: none;
          opacity: 0; animation: wordmarkIn .5s ease 1.6s forwards;
        }
        .splash-cap {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px; letter-spacing: .18em; text-transform: uppercase;
          color: var(--ink-3);
          opacity: 0; animation: capIn .5s ease 2.5s forwards;
        }
        @keyframes wordmarkIn { from { opacity: 0; transform: translate(-50%, calc(50% + 4px)) } to { opacity: 1; transform: translate(-50%, 50%) } }
        @keyframes capIn { from { opacity: 0 } to { opacity: 1 } }
        .splash-cap .dots span { animation: dotBlink 1.4s infinite; opacity: .2; }
        .splash-cap .dots span:nth-child(1) { animation-delay: 0s; }
        .splash-cap .dots span:nth-child(2) { animation-delay: .2s; }
        .splash-cap .dots span:nth-child(3) { animation-delay: .4s; }
        @keyframes dotBlink { 0%, 100% { opacity: .2 } 50% { opacity: 1 } }

        @media (prefers-reduced-motion: reduce) {
          /* Land on the finished frame: fully drawn maze, static heart,
             wordmark + caption visible with no entrance delays.
             (!important beats the per-segment inline dashoffset.) */
          .splash-wrap::before, .splash-wrap::after { transition: none; }
          .maze-seg { animation: none; stroke-dashoffset: 0 !important; }
          .splash-heart, .splash-halo, .splash-cap .dots span { animation: none; }
          .splash-wordmark, .splash-cap { animation: none; opacity: 1; }
        }
      `}</style>
    </div>
  );
}
